<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Animierter Background -->
    <div class="fixed inset-0 -z-10">
      <!-- Gradient Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 dark:from-gray-900 dark:via-blue-950/20 dark:to-gray-800"></div>
      
      <!-- Animierte Gradient Blobs -->
      <div class="absolute top-0 -left-4 w-96 h-96 bg-blue-400/20 dark:bg-blue-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob"></div>
      <div class="absolute top-0 -right-4 w-96 h-96 bg-purple-400/20 dark:bg-purple-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      <div class="absolute -bottom-8 left-20 w-96 h-96 bg-pink-400/20 dark:bg-pink-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
      
      <!-- Grid Pattern Overlay -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)]"></div>
    </div>
    
    <Navigation />
    
    <!-- Main Content -->
    <section class="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-32 md:pt-40 pb-20">
      <div 
        ref="contentElement"
        class="transition-all duration-700 ease-out"
        :class="contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
      >
        <!-- Header -->
        <div class="text-center mb-12">
          <h1 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Impressum
          </h1>
          <div class="mt-4 h-0.5 w-16 bg-blue-600 dark:bg-blue-400 mx-auto"></div>
        </div>

        <!-- Content Card -->
        <div class="bg-white dark:bg-gray-800 p-8 md:p-10 rounded-xl shadow-lg dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700">
          <div class="prose prose-gray dark:prose-invert max-w-none">
            <!-- Betreiber -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Betreiber dieser Website</h2>
              <div class="space-y-2 text-gray-700 dark:text-gray-300">
                <p><strong>Name:</strong> Maximilian Reitsberger</p>
                <p><strong>Adresse:</strong><br>
                  Marktplatz 5<br>
                  85598 Baldham<br>
                  Deutschland</p>
                <p class="mt-4">
                  <strong>Kleingewerblicher Betrieb</strong> gemäß § 19 UStG (Kleinunternehmerregelung)
                </p>
              </div>
            </section>

            <!-- Kontakt -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Kontakt</h2>
              <div class="space-y-2 text-gray-700 dark:text-gray-300">
                <p>
                  <strong>E-Mail:</strong> 
                  <a 
                    :href="`mailto:${contactEmail}`" 
                    class="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 transition-colors"
                  >
                    {{ contactEmail }}
                  </a>
                </p>
              </div>
            </section>

            <!-- Verantwortlich für den Inhalt -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV
              </h2>
              <div class="space-y-2 text-gray-700 dark:text-gray-300">
                <p>Maximilian Reitsberger</p>
                <p>
                  Marktplatz 5<br>
                  85598 Baldham<br>
                  Deutschland
                </p>
              </div>
            </section>

            <!-- Haftungsausschluss -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Haftungsausschluss</h2>
              
              <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">Haftung für Inhalte</h3>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.
              </p>

              <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">Haftung für Links</h3>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar.
              </p>

              <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">Urheberrecht</h3>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.
              </p>
              <!-- Streitbeilegung -->
            <section class="mb-8">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Streitbeilegung</h2>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
                <a 
                  href="https://ec.europa.eu/consumers/odr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  https://ec.europa.eu/consumers/odr
                </a>.<br>
                Unsere E-Mail-Adresse finden Sie oben im Impressum.
              </p>
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </section>
            </section>
          </div>
        </div>

        <!-- Back Link -->
        <div class="mt-8 text-center">
          <router-link 
            to="/contact" 
            class="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 transition-colors font-medium"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Zurück zur Kontaktseite
          </router-link>
        </div>
      </div>
    </section>

    <StandardFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Navigation from '../components/Navigation.vue'
import StandardFooter from '../components/StandardFooter.vue'
import { useScrollAnimation } from '../composables/useScrollAnimation'
import { getContactInfo, getDefaultContactInfo } from '../firebase/contact'
import { logger } from '../utils/logger'

const contactEmail = ref('info@reitsberger-it.de')

// Scroll-Animationen
const { isVisible: contentVisible, targetElement: contentElement } = useScrollAnimation({
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
})

onMounted(async () => {
  try {
    const data = await getContactInfo()
    if (data?.email) {
      contactEmail.value = data.email
    } else {
      const defaultData = getDefaultContactInfo()
      if (defaultData.email && defaultData.email !== 'kontakt@example.com') {
        contactEmail.value = defaultData.email
      }
    }
  } catch (error) {
    logger.error('Fehler beim Laden der Kontaktdaten', error)
  }
})
</script>

<style scoped>
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}
.animate-blob {
  animation: blob 7s infinite;
}
.animation-delay-2000 {
  animation-delay: 2s;
}
.animation-delay-4000 {
  animation-delay: 4s;
}
</style>

